import React from "react";
import { Modal, Form, Button, Badge, InputGroup } from "react-bootstrap";
import ProSourceForm from "../../../components/ProSourceForm";
import { get } from "lodash";
import { tryParse } from "../../../helpers";

const ALLERGY_OPTIONS = [
  "Peanuts",
  "Lactose Intolerant",
  "Mushrooms",
  "Shellfish",
  "Soy",
  "Eggs",
  "Wheat",
];

const PREFERENCE_OPTIONS = [
  "Diabetic",
  "Vegetarian",
  "Pescatarian",
  "Kosher",
  "Pregnant",
];

const purpleColor = "#8950fc";

const basePillStyle = {
  margin: "0.25rem",
  padding: "0.5rem 0.75rem",
  cursor: "pointer",
  fontSize: "0.9rem",
  fontWeight: "500",
  transition: "all 0.2s ease",
  border: "none",
  borderRadius: "50rem",
};

const experiencePillStyle = {
  ...basePillStyle,
  backgroundColor: "#f0f0f0",
  color: "#555",
};

const activeExperiencePillStyle = {
  ...basePillStyle,
  backgroundColor: purpleColor,
  color: "#ffffff",
};

export default class EditDietaryModal extends ProSourceForm {
  constructor(props) {
    super(props);
    const { reservation } = props;
    const notes = tryParse(reservation.notes) || {};

    this.state = {
      values: {
        dietary: get(notes, "dietary", []),
        otherNotes: "",
      },
      errors: {},
      touched: {},
      isSubmitting: false,
    };
  }

  handleSelectDietaryItem = (itemName) => {
    const { values } = this.state;
    const currentItems = [...values.dietary];
    const existingItemIndex = currentItems.findIndex(
      (item) => item.label.toLowerCase() === itemName.toLowerCase(),
    );

    let newItems;
    if (existingItemIndex > -1) {
      newItems = currentItems.filter(
        (item) => item.label.toLowerCase() !== itemName.toLowerCase(),
      );
    } else {
      newItems = [...currentItems, { label: itemName, count: 1 }];
    }

    this.handleOnChange({ target: { name: "dietary", value: newItems } });
  };

  handleAddCustomItem = () => {
    const { values } = this.state;
    const itemName = values.otherNotes.trim();
    if (!itemName) return;

    const itemExists = values.dietary.some(
      (item) => item.label.toLowerCase() === itemName.toLowerCase(),
    );

    if (itemExists) {
      this.handleOnChange({ target: { name: "otherNotes", value: "" } });
      return;
    }

    const newItems = [...values.dietary, { label: itemName, count: 1 }];
    this.handleOnChange({ target: { name: "dietary", value: newItems } });
    this.handleOnChange({ target: { name: "otherNotes", value: "" } });
  };

  handleUpdateCount = (itemName, newCount) => {
    const { values } = this.state;
    let newItems;
    if (newCount < 1) {
      newItems = values.dietary.filter(
        (item) => item.label.toLowerCase() !== itemName.toLowerCase(),
      );
    } else {
      newItems = values.dietary.map((item) =>
        item.label.toLowerCase() === itemName.toLowerCase()
          ? { ...item, count: newCount }
          : item,
      );
    }
    this.handleOnChange({ target: { name: "dietary", value: newItems } });
  };

  onSubmit = (values, setSubmitting) => {
    this.props.onSubmit(values, setSubmitting);
  };

  render() {
    const { show, onHide } = this.props;
    const { values, isSubmitting } = this.state;
    const inputActions = {
      onChange: this.handleOnChange,
      onBlur: this.handleOnBlur,
    };

    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Dietary Notes</Modal.Title>
        </Modal.Header>
        <Form onSubmit={this.handleOnSubmit}>
          <Modal.Body>
            <Form.Group
              className="mb-3 p-3 border rounded"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <Form.Label className="font-weight-bold">
                Dietary Preference
              </Form.Label>
              <div className="mb-2">
                <Form.Label as="small" className="text-muted">
                  Allergies
                </Form.Label>
                <div>
                  {ALLERGY_OPTIONS.map((option) => (
                    <Badge
                      key={option}
                      pill
                      style={
                        values.dietary.some((item) => item.label === option)
                          ? activeExperiencePillStyle
                          : experiencePillStyle
                      }
                      onClick={() => this.handleSelectDietaryItem(option)}
                    >
                      {option}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <Form.Label as="small" className="text-muted">
                  Preferences
                </Form.Label>
                <div>
                  {PREFERENCE_OPTIONS.map((option) => (
                    <Badge
                      key={option}
                      pill
                      style={
                        values.dietary.some((item) => item.label === option)
                          ? activeExperiencePillStyle
                          : experiencePillStyle
                      }
                      onClick={() => this.handleSelectDietaryItem(option)}
                    >
                      {option}
                    </Badge>
                  ))}
                </div>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="otherNotesInput">
                Other Dietary Notes
              </Form.Label>
              <InputGroup>
                <Form.Control
                  id="otherNotesInput"
                  name="otherNotes"
                  value={values.otherNotes}
                  style={{ backgroundColor: "#f8f9fa" }}
                  placeholder="e.g., Tomato"
                  onChange={inputActions.onChange}
                  onBlur={inputActions.onBlur}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      this.handleAddCustomItem();
                    }
                  }}
                />
                <Button
                  variant="outline-secondary"
                  onClick={this.handleAddCustomItem}
                >
                  Add
                </Button>
              </InputGroup>
            </Form.Group>

            {values.dietary.length > 0 && (
              <div
                className="mb-3 p-3 border rounded"
                style={{ backgroundColor: "#ffffff" }}
              >
                {values.dietary.map((item, index) => (
                  <div
                    key={item.label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.5rem 0",
                      borderBottom:
                        index === values.dietary.length - 1
                          ? "none"
                          : "1px solid #eee",
                    }}
                  >
                    <span style={{ fontWeight: "500" }}>{item.label}</span>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Button
                        variant="light"
                        size="sm"
                        style={{
                          padding: "0.25rem 0.6rem",
                          margin: "0 0.25rem",
                          lineHeight: 1,
                        }}
                        onClick={() =>
                          this.handleUpdateCount(item.label, item.count - 1)
                        }
                      >
                        -
                      </Button>
                      <span
                        style={{
                          minWidth: "20px",
                          textAlign: "center",
                          fontWeight: "bold",
                          margin: "0 0.25rem",
                        }}
                      >
                        {item.count}
                      </span>
                      <Button
                        variant="light"
                        size="sm"
                        style={{
                          padding: "0.25rem 0.6rem",
                          margin: "0 0.25rem",
                          lineHeight: 1,
                        }}
                        onClick={() =>
                          this.handleUpdateCount(item.label, item.count + 1)
                        }
                      >
                        +
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={onHide} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              style={{ backgroundColor: purpleColor, borderColor: purpleColor }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}
